import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Storage,
  QueryStats,
  CloudQueue,
  Refresh,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import api from '../../services/api';
import { useEntityContext } from '../EntityContextSwitch';

// eslint-disable-next-line max-lines-per-function
function DataSummary() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const { currentEntity } = useEntityContext();

  const clientId = currentEntity?.id;
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    if (clientId) {
      fetchSummary();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEntityDataSummary(clientId);
      setSummary(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching data summary:', err);
      setError(err.message || 'Failed to load data summary');
    } finally {
      setLoading(false);
    }
  };

  const calculateStoragePercentage = () => {
    if (!summary || !summary.totalDataSize) return 0;
    // Assuming a 10GB limit for visualization purposes
    const limitBytes = 10 * 1024 * 1024 * 1024; // 10GB
    return Math.min((summary.totalDataSize / limitBytes) * 100, 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info">
        No data summary available.
      </Alert>
    );
  }

  const dataSourceCards = [
    {
      key: 'gcs',
      title: 'Google Cloud Storage',
      icon: <Storage fontSize="large" />,
      data: summary.dataSources.gcs,
      color: '#4285F4'
    },
    {
      key: 'bigquery',
      title: 'BigQuery',
      icon: <QueryStats fontSize="large" />,
      data: summary.dataSources.bigquery,
      color: '#EA4335'
    },
    {
      key: 'firestore',
      title: 'Firestore',
      icon: <CloudQueue fontSize="large" />,
      data: summary.dataSources.firestore,
      color: '#34A853'
    }
  ];

  return (
    <Box>
      {/* Header with refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Data Overview for {summary.clientName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastRefresh && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchSummary} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Overall Statistics */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Typography variant="h6" color="white" gutterBottom>
            Total Data Usage
          </Typography>
          <Typography variant="h3" color="white" sx={{ mb: 2 }}>
            {summary.totalDataSizeFormatted}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={calculateStoragePercentage()}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
          <Typography variant="caption" color="white" sx={{ mt: 1, display: 'block' }}>
            {calculateStoragePercentage().toFixed(1)}% of estimated 10GB limit
          </Typography>
        </CardContent>
      </Card>
      {/* Data Sources Grid */}
      <Grid container spacing={3}>
        {/* eslint-disable-next-line max-lines-per-function */}
        {dataSourceCards.map((source) => (
          <Grid
            key={source.key}
            size={{
              xs: 12,
              md: 4
            }}>
            <Card
              sx={{
                height: '100%',
                borderTop: `4px solid ${source.color}`,
                opacity: source.data.available ? 1 : 0.6
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ color: source.color }}>
                    {source.icon}
                  </Box>
                  <Chip
                    icon={source.data.available ? <CheckCircle /> : <Cancel />}
                    label={source.data.available ? 'Active' : 'Not Provisioned'}
                    color={source.data.available ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  {source.title}
                </Typography>

                {source.data.available ? (
                  <Stack spacing={1}>
                    {source.key === 'gcs' && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Files
                          </Typography>
                          <Typography variant="h5">
                            {source.data.fileCount || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Storage Used
                          </Typography>
                          <Typography variant="body1">
                            {source.data.totalSizeFormatted || '0 Bytes'}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {source.key === 'bigquery' && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Tables
                          </Typography>
                          <Typography variant="h5">
                            {source.data.tableCount || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Rows
                          </Typography>
                          <Typography variant="body1">
                            {source.data.totalRows?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Data Size
                          </Typography>
                          <Typography variant="body1">
                            {source.data.totalBytesFormatted || '0 Bytes'}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {source.key === 'firestore' && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Documents
                          </Typography>
                          <Typography variant="h5">
                            {source.data.documentCount || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Estimated Size
                          </Typography>
                          <Typography variant="body1">
                            {source.data.estimatedSizeFormatted || '0 Bytes'}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This data source has not been provisioned for this client.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Client Status */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Client Information
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Typography variant="caption" color="text.secondary">
                Client ID
              </Typography>
              <Typography variant="body1">
                {summary.clientId}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={summary.status || 'Unknown'}
                color={summary.status === 'active' ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {summary.created ? new Date(summary.created).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Typography variant="caption" color="text.secondary">
                Last Activity
              </Typography>
              <Typography variant="body1">
                {summary.lastUpdated ? new Date(summary.lastUpdated).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

export default DataSummary;